import * as React from 'react'
import { SectionListData, SectionList, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Checkbox from '@components/checkbox/Checkbox'

interface Option<T> {
  value: T
  label: string
}

interface Props<T> {
  data: ReadonlyArray<SectionListData<Option<T>, { title: string }>>
  onItemSelected: (values: T) => void
  onSectionSelected: (values: T[], clear: boolean) => void
  selected: T[]
  background?: string
}

const SectionPicker: <T>(props: Props<T>) => React.ReactElement = ({
  data,
  onItemSelected,
  onSectionSelected,
  selected,
  background = theme.colors.background,
}) => {
  const items = React.useMemo(() => data.filter((d) => d.data.length > 0), [data])
  const size = React.useMemo(() => items.reduce((acc, cur) => acc + cur.data.length, 0), [items])

  const renderItem = ({ item }: { item: Option<(typeof selected)[number]> }) => {
    const isSelected = selected.includes(item.value)

    return (
      <Item>
        <Checkbox label={item.label} onClick={() => onItemSelected(item.value)} value={isSelected} />
      </Item>
    )
  }

  const renderSection = ({ section }: { section: (typeof data)[number] }) => {
    const isFull = !section.data.find((item) => !selected.includes(item.value))

    return (
      <Section background={background}>
        <Checkbox
          label={section.title}
          onClick={() =>
            onSectionSelected(
              section.data.map((item) => item.value),
              isFull
            )
          }
          value={isFull}
          fontBold
        />
      </Section>
    )
  }

  return (
    <SectionList
      key={`data_${size}`}
      sections={items}
      keyExtractor={(item) => JSON.stringify(item.value)}
      renderItem={renderItem}
      renderSectionHeader={renderSection}
      extraData={selected}
    />
  )
}

export default SectionPicker

const Section = styled(View)<{ background: string }>`
  background-color: ${(props) => props.background};
  padding: 5px 0;
`

const Item = styled(View)`
  margin-left: 15px;
  padding: 5px 0;
`
